import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { BRANCH_LABEL } from "../../components/common";

const ACT = { new: ["New", "text-emerald-600"], fill: ["Fill empty details", "text-sky-600"], no_change: ["Nothing new", "text-muted-foreground"], error: ["Problem", "text-rose-600"] };

const downloadTemplate = () => api.get("/hr/import/template", { responseType: "blob" }).then((r) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = "axiom-employee-import-template.xlsx";
  a.click();
  URL.revokeObjectURL(a.href);
}).catch((e) => toast.error(errMsg(e)));

const Counts = ({ c, done }) => (
  <div className="flex flex-wrap gap-4 text-sm" data-testid="import-counts">
    <span className="text-emerald-600">{c.new} new</span><span className="text-sky-600">{c.fill} filled in</span>
    <span className="text-muted-foreground">{c.no_change} nothing new</span><span className="text-rose-600">{c.error} with problems</span>
    <span>{c.logins} app logins {done ? "given" : "to give"}</span>
  </div>
);

const Preview = ({ res }) => (
  <div className="ax-panel overflow-x-auto max-h-[45vh]"><table className="ax-table w-full min-w-[720px]" data-testid="import-preview-table">
    <thead><tr><th>Row</th><th>Emp ID</th><th>Name</th><th>What happens</th><th>App login</th></tr></thead>
    <tbody>{res.rows.map((r) => (
      <tr key={r.row} data-testid={`import-row-${r.row}`}>
        <td className="num text-xs">{r.row}</td><td className="font-mono text-xs">{r.emp_code || "—"}{r.auto_code && <span className="text-[10px] text-muted-foreground font-sans"> (next number)</span>}</td>
        <td className="text-sm">{r.name || "—"}</td>
        <td className={`text-xs ${ACT[r.action][1]}`}>{r.error || ACT[r.action][0]}{r.action === "fill" && r.fields?.length ? `: ${r.fields.join(", ")}` : ""}{r.branch ? ` · ${BRANCH_LABEL[r.branch] || r.branch}` : ""}</td>
        <td className="text-xs">{r.login ? `${r.login.role_name}${r.login.status === "exists" ? " (already has a login, unchanged)" : ""}` : "—"}</td>
      </tr>))}</tbody>
  </table></div>
);

export const ImportDialog = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (commit) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/hr/import?commit=${commit}`, fd);
      setRes(r.data);
      if (commit) { toast.success(`Imported: ${r.data.counts.new} new, ${r.data.counts.fill} filled in`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ready = res && !res.committed && res.counts.new + res.counts.fill + res.counts.logins > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-card" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display">Import employees from Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Use the template's column names. Existing employee IDs (or emails) only get their empty details filled in; nothing HR typed is replaced. Leave Employee ID blank for new joiners to get the next number. Rows with an App role and official email get a login (never Founder or System Administrator).</p>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="import-template-button" onClick={downloadTemplate} className="border h-9 px-3 text-sm flex items-center gap-2 hover:border-primary"><Download className="h-4 w-4" />Download template</button>
          <label className="border h-9 px-3 text-sm flex items-center gap-2 cursor-pointer hover:border-primary"><FileSpreadsheet className="h-4 w-4" />
            <span data-testid="import-file-name">{file ? file.name : "Choose .xlsx file"}</span>
            <input data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setRes(null); }} /></label>
          <button data-testid="import-check-button" disabled={!file || busy} onClick={() => send(false)} className="bg-primary text-primary-foreground h-9 px-4 text-sm disabled:opacity-50">{busy && !res ? "Checking…" : "Check file"}</button>
        </div>
        {res && <><Counts c={res.counts} done={res.committed} />
          {res.ignored_columns.length > 0 && <div className="text-xs text-amber-600" data-testid="import-ignored">Columns not used: {res.ignored_columns.join(", ")}</div>}
          <Preview res={res} /></>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border h-9 px-4 text-sm" data-testid="import-close">{res?.committed ? "Done" : "Cancel"}</button>
          {ready && <button data-testid="import-confirm-button" disabled={busy} onClick={() => send(true)} className="bg-primary text-primary-foreground h-9 px-4 text-sm">{busy ? "Importing…" : "Import these rows"}</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
